import {
  ApolloCache,
  BackgroundQueryHookOptions,
  InMemoryCache,
  makeVar,
  skipToken,
  TypedDocumentNode
} from '@apollo/client';

import { client } from '@hooks/useApolloClient';
import { StorageKey } from '@store/types';
import { getStorageItem, setStorageItem } from '@utils/helperFunctions';

/**
 * @summary Returns skipToken or options depending on condition truthiness
 * @param condition
 * @param options
 * @see [Apollo skipToken docs](https://www.apollographql.com/docs/react/api/react/hooks/#skiptoken)
 */
export const executeOrSkip = <TData, TVariables extends Record<string, any>>
  (condition: boolean, options: BackgroundQueryHookOptions<TData, TVariables>) => (
    Boolean(condition) ? options : skipToken
  );

export const addToCache = <ApolloQueryType extends Record<string, unknown>>(
  data: Partial<ApolloQueryType>,
  query: TypedDocumentNode,
  cache: ApolloCache<any>,
  field: keyof ApolloQueryType
) => {
  const existing = cache.readQuery<
  Record<keyof ApolloQueryType, Array<typeof data>>
  >({
    query
  });

  if (data && existing) {
    cache.writeQuery({
      query,
      data: {
        [field]: [...existing[field as any], data]
      }
    });
  }
};

export const removeFromCache = (
  keyField: string, // primary key of object
  fieldIdValue: string | number,
  cache: ApolloCache<any>,
  typename: string
) => {
  const normalizedId = cache.identify({ [keyField]: fieldIdValue, __typename: typename });
  cache.evict({ id: normalizedId });
};

/**
 * @summary A reactive var that mirrors its value into sessionStorage on every change.
 *
 * Apollo's `onNextChange` is a one-shot listener: after it fires, the subscription is
 * removed. The previous implementation only persisted the FIRST mutation after boot —
 * every subsequent update was lost on reload. Re-subscribing inside the listener
 * keeps the persistence going for the lifetime of the var.
 */
export const makePersistedVar = <TValue>(
  storageKey: StorageKey,
  defaultValue: TValue
) => {
  const reactiveVar = makeVar<TValue>(
    getStorageItem<TValue>(storageKey) ?? defaultValue
  );

  const subscribe = (): void => {
    reactiveVar.onNextChange((value) => {
      setStorageItem(storageKey, value);
      // Re-arm: onNextChange auto-detaches after firing.
      subscribe();
    });
  };
  subscribe();

  return reactiveVar;
};

export const resetCache = async (
  cache: InMemoryCache
) => {
  await cache.reset({ discardWatches: false })
    .catch((error) => {
      console.warn(error);
    });
  return client.resetStore();
};

// const buildQuery = <T>(gql: TypedDocumentNode<T>, variables: Record<string, any>): string => {
//   const query = gql.loc!.source.body;
//   const variableString = Object.keys(variables).map((key) => `$${key}: ${typeof variables[key]}`).join(', ');
//   const variableValues = Object.keys(variables).map((key) => `${key}: $${key}`).join(', ');
//
//   return `
//     query ${query.definitions[0].name.value}(${variableString}) {
//       ${query.definitions[0].selectionSet.selections.map((selection) => {
//     if (selection.kind === 'Field') {
//       return `${selection.name.value} { ${selection.selectionSet.selections.map((field) => field.name.value).join(', ')} }`;
//     }
//     return '';
//   }).join('\n')}
//     }
//   `;
// };
